/* eslint-disable import/named */
// TODO: Figure out why turf imports are not found.
import {
  PlanningBlocks,
  SchoolPlusLevel,
} from "../../../editing_planning/data_requirements";
import {
  Feature,
  FeatureCollection,
  union,
  Polygon,
  MultiPolygon,
} from "@turf/turf";
import { useBlocks, useBlockShapeData } from "../../../api/seedData";
import { useEffect, useState } from "react";
import { useMapMode } from "../../../utils/router";
import { useSub } from "../../../events/events";
import { EVENTS } from "../../../events/eventConstants";
import { useEditingContextStore } from "../../../context/EditingContext";

interface FeaturesBySchool {
  [key: SchoolPlusLevel]: Feature<Polygon | MultiPolygon>;
}

export const useCreateBoundaryForAllSchools = () => {
  const [initialLoad, setInitialLoad] = useState<boolean>(false);
  const shapeBoundaries = useBlockShapeData();
  const mode = useMapMode();
  const { resetUpdatedMetrics } = useEditingContextStore();
  const [features, setFeatures] = useState<FeaturesBySchool>();
  const blocks = useBlocks();

  // Subscribe to clearTables event. Reload all boundaries.
  useSub(EVENTS.CLEAR_TABLES, () => {
    // TODO: ALso clear selection!
    setInitialLoad(false);
    resetUpdatedMetrics();
  });
  useSub(
    EVENTS.UPDATE_TABLES,
    ({ schoolsToUpdate, newBlocks }) => {
      if (!initialLoad || !shapeBoundaries) return;
      if (!schoolsToUpdate) return;
      const allShapes = calculateBoundaries(
        shapeBoundaries,
        newBlocks,
        features,
        schoolsToUpdate,
      );
      setFeatures(allShapes);
    },
    [shapeBoundaries, blocks],
  );

  // Load + calculate all boundaries for the first time
  useEffect(() => {
    // Don't load boundaries until edit mode is selected.
    if (mode !== "edit") return;
    if (initialLoad || !shapeBoundaries || !blocks) return;
    const allShapes = calculateBoundaries(shapeBoundaries, blocks);
    setFeatures(() => allShapes);
    setInitialLoad(true);
  }, [shapeBoundaries, blocks, mode]);
  return features;
};

const calculateBoundaries = (
  shapeBoundaries: FeatureCollection<
    Polygon,
    {
      [name: string]: number | string;
    }
  >,
  blocks: PlanningBlocks,
  existingBoundaries?: FeaturesBySchool,
  schoolsToUpdate?: SchoolPlusLevel[],
): {
  [key: SchoolPlusLevel]: Feature<Polygon | MultiPolygon>;
} => {
  const allShapes = Object.assign({}, existingBoundaries);
  schoolsToUpdate?.forEach((schoolID) => {
    allShapes[schoolID] = undefined;
  });

  shapeBoundaries?.features.forEach((feature) => {
    const currPBIDFeature = feature.properties["pb_id"];
    const currPBID =
      typeof currPBIDFeature === "string"
        ? parseInt(currPBIDFeature)
        : currPBIDFeature;
    const block = blocks?.[currPBID];
    ["ES", "MS", "HS"].forEach((lvl, index) => {
      if (
        schoolsToUpdate &&
        !schoolsToUpdate.includes(`${block[`${lvl.toLowerCase()}_id`]}:${lvl}`)
      ) {
        return;
      }
      // Get the ID of the schoolPlusLevel which this block belongs to.
      // If the ID is present in the changes, then update. Otherwise, keep the same.
      const id = `${block[`${lvl.toLowerCase()}_id`]}:${lvl}`;
      if (!allShapes[id]) {
        allShapes[id] = {
          type: "Feature",
          geometry: { type: "MultiPolygon", coordinates: [] },
          properties: {},
        };
      }

      allShapes[id] = union(allShapes[id], feature);
      allShapes[id].properties = {
        level: lvl,
        index: index,
        id: id,
        schoolID: block[`${lvl.toLocaleLowerCase()}_id`],
      };
    });
  });
  return allShapes;
};
